import type {ThemeId, ThemePreference} from "@project/design-system-core";

import {useSyncExternalStore} from "react";

import {WebThemeManager} from "../utils/web-theme-manager";

export interface UseThemeResult {
  /** The currently active resolved theme (never 'system'). */
  theme: ThemeId;
  /** The user-stored preference (may be 'system'). */
  preference: ThemePreference;
  /** Persist and apply a new preference. */
  setPreference: (preference: ThemePreference) => Promise<void>;
}

interface ThemeSnapshot {
  theme: ThemeId;
  preference: ThemePreference;
}

/**
 * useSyncExternalStore requires getSnapshot to return the SAME reference
 * when the store has not changed. Returning a new object literal on every
 * call causes React to detect a change on every render → infinite loop.
 *
 * We cache the last snapshot here (module-level, tied to the singleton)
 * and only allocate a new object when a value actually differs.
 */
let cachedSnapshot: ThemeSnapshot | null = null;

function getSnapshot(): ThemeSnapshot {
  const manager = WebThemeManager.getInstance();
  const theme = manager.getActiveTheme();
  const preference = manager.getPreference();

  if (
    cachedSnapshot === null ||
    cachedSnapshot.theme !== theme ||
    cachedSnapshot.preference !== preference
  ) {
    cachedSnapshot = {preference, theme};
  }

  return cachedSnapshot;
}

/**
 * React hook for reading and updating the active theme.
 *
 * Backed by WebThemeManager (singleton) via useSyncExternalStore.
 * Components using this hook will re-render when the theme changes,
 * whether triggered by setPreference() or an OS color scheme change.
 *
 * No React Context needed — HeroUI v3 theming is CSS-attribute based.
 */
export function useTheme(): UseThemeResult {
  const manager = WebThemeManager.getInstance();

  const snapshot = useSyncExternalStore(
    (onStoreChange: () => void) => manager.subscribe(() => onStoreChange()),
    getSnapshot,
    getSnapshot, // SSR safety — same function is fine for this SPA
  );

  const setPreference = (preference: ThemePreference) => manager.setPreference(preference);

  return {
    preference: snapshot.preference,
    setPreference,
    theme: snapshot.theme,
  };
}
